
import * as d3 from 'd3';
import d3plus from 'd3plus-text';
import { toPoly } from './topoly';
import { largestRect } from './largest-rect';

import byPasses from './labelarea-bypass.json';

const byPassMaps = {};
const getByPassMap = scid => {
  if (!byPassMaps[scid]) {
    const { areas } = byPasses[scid] || { areas: [] };
    byPassMaps[scid] = areas.reduce((acc, [id, cx, cy, angle, w, h]) => {
      acc[id] = [cx, cy, angle, -w/2, -h/2, w, h];
      return acc;
    }, {});
  }
  return byPassMaps[scid];
};

const DEBUG_ID = 'storePath-VU1R02';

const defaults = {
  angle: [0, 90], // Only allowing two possible angles for a label
  minWidth: 20,
  minHeight: 8,
  tolerance: 0 // no simplification by default
};

/**
 Will add store labels into biggest rectangle areas that with corres ponding
 store element.
 */
export function addStoreLabels(map, storesSel, data, angleOffset = 0, scid) {

  const transformAngle = transformAngleBy(angleOffset);
  const transformByPassAngle = (x => x >= -90 && x < 90)(angleOffset) ?
    transformAngle : transformAngleBy(angleOffset - 90);
  const defaultOptions = Object.assign({}, defaults,
    { angle: defaults.angle.map(transformAngle) }
  );
  const areaByPasses = getByPassMap(scid);

  map = d3.select(map);
  const stores = map.selectAll(storesSel);
  const maxLabelAreas = getMaxLabelAreas(data, stores, defaultOptions, areaByPasses, transformByPassAngle)
    .filter(label => 'cx' in label && 'cy' in label && 'angle' in label);

  // Add layer
  const labelsGroup = map.append('g').attr('id', 'labels');
  if(maxLabelAreas.length > 0) {
    addLabels(labelsGroup, maxLabelAreas);
  }
}

/**
  Transforms angles with 90 degree step function.
 */
export const transformAngleBy = angleOffset => {

  const between = (lower, upper, value) => value > lower && value <= upper;

  // collapse multiples full circles
  const angleChange = angleOffset >= 0 ?
    angleOffset % 360 :
    360 + (angleOffset % 360); // Negative offset to positive

  const angleStepLimited =
    between(0, 90, angleChange) ? 90 :
      between(90, 180, angleChange) ? 180 :
        between(180, 270, angleChange) ? 270 :
        /*between(270, 360, angleChange)*/  0;

  return angle => angle - angleStepLimited;
};

/*^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
 * LABEL BOX LOOKUP
 *----------------------------------------------------------------------------*/

const getBBoxCenter = element => {
  const { x, y, width, height } = element.getBBox();
  return [x + width/2, y + height/2];
};

const getOrigin = element => {
  switch (element.tagName) {
  // Simple shapes
  case 'rect':
  case 'circle':
  case 'ellipse': return getBBoxCenter(element);
  // Complex shapes have to rely on random origin lookup
  case 'path':
  case 'polygon': return null;
  default: console.warn('Unhandled element', element); return null;
  }
};

/**
 *
 */
function getMaxLabelAreas(data, storesD3, defaultOptions, areaByPasses, transformAngle) {
  return storesD3.nodes().map(node => {
    const datum = data.find(d => d.id == node.id) || {};
    const id = (datum.id || '').replace('storePath-', '');

    if (areaByPasses[id]) {
      const [cx, cy, angle, tx, ty, width, height] = areaByPasses[id];
      return Object.assign({}, { cx, cy, angle: transformAngle(angle), tx, ty, width, height }, datum);
    }

    const poly = toPoly(node);
    const origin = getOrigin(node); // starting point for label placement calculation
    const debug = datum.id === DEBUG_ID;
    const opts = Object.assign({}, defaultOptions, { origin, vdebug: debug } );
    const { maxRect, events } = largestRect(poly, opts);

    if (!maxRect) {
      console.warn('Problem with map element', node.id);
    }
    if (debug) {
      console.log(`Largest rect debug for '${datum.id}'`, events);
      // return Object.assign({}, { cx: 902, cy: 480, angle: -120, tx: -42, ty: -12, width: 80, height: 25 }, datum);
    }

    return Object.assign({}, maxRect, datum);
  });
}


/*^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
 * RENDERING
 *----------------------------------------------------------------------------*/

const storeKey = (_,index,list) => list[index].id;

function addLabels(layer, data) {
  const labels = layer.selectAll('.store-label')
    .data(data, storeKey)
    .enter()
    .append('g')
    .attr('class','store-label')
    .attr('transform', d => `translate(${d.cx} ${d.cy}) rotate(${d.angle})`);

  addLabelOrigin(labels);
  addLabelBox(labels);
  addLabelText(labels);
}

function addLabelOrigin(labels) {
  labels
    .append('circle')
    .attr('class','label__origin')
    .attr('r', 3);
}

function addLabelBox(labels) {
  labels
    .append('rect')
    .attr('class', 'label__area')
    .attr('transform', d => `translate(${d.tx} ${d.ty})`)
    .attr('width', d => d.width)
    .attr('height', d => d.height);
}

const getStyles = (elem, ...props) => {
  const styles = window.getComputedStyle(elem);
  return props.reduce(
    (map, style) => (map[style] = styles.getPropertyValue(style), map), {}
  );
};

function addLabelText(labels) {

  const styles = getStyles(labels.node(), 'font-family');
  const fontFamily = styles['font-family'];

  labels.each((data, index, nodes) => {
    //console.log(index, data)

    preprocessLabelText(data);

    new d3plus.TextBox()
      .data([data])
      .select(nodes[index]) // Label group
      .text(d => d.name)
      .textAnchor('middle')
      .verticalAlign('middle')
      .fontFamily(fontFamily)
      .fontMax(24)
      .fontMin(5)
      .fontSize(16)
      .fontResize(true)
      .lineHeight(0.3)
      .width(d => d.width)
      .height(d => d.height)
      .x(d => d.tx)
      .y(d => d.ty)
      .render();
  });
}

function preprocessLabelText(data) {
  preprocessAmpersands(data);
}

/* Replace independently occurring ampersands into words using a zero-width space character (non-whitespace) */
function preprocessAmpersands(data) {
  let name = data.name;

  if (name && name.indexOf(' & ') > 0) {
    data.name = name.replace(' & ', ' &\u200B ');
  }
}
